import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircleLoader } from "react-spinners";
import { TailSpin } from "react-loader-spinner";
import { LuAlertTriangle } from "react-icons/lu";
import Icon from "../../assets/icons";
import useRequest from "../../components/hooks/use-request";
import { showToast } from "../../components/toast";
import RichTextEditor from "./rich-text-editor";
import EmailPreviewPane from "./email-preview";
import ConfirmModal from "./confirm-modal";
import StatusPill from "./status-pill";
import {
  EmailPreview,
  EmailTemplate,
  KNOWN_CATEGORIES,
  currentAdminId,
  toMessage,
} from "./api";

const inputCls =
  "w-full h-[42px] px-3 border border-[#0979A1] rounded-lg text-[14px] outline-none focus:border-[#0979A1]";
const labelCls = "text-[13px] font-medium text-[#040821] mb-1.5 block";

const TemplateEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "new";

  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("newsletter");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [loaded, setLoaded] = useState(isNew);
  const [approveOpen, setApproveOpen] = useState(false);

  const { makeRequest: getTemplate, loading: loadingTemplate } = useRequest(
    `/email-templates/${id}`,
    "GET"
  );
  const { makeRequest: createTemplate, loading: creating } = useRequest(
    "/email-templates",
    "POST"
  );
  const { makeRequest: updateTemplate, loading: updating } = useRequest(
    `/email-templates/${id}`,
    "PATCH"
  );
  const { makeRequest: getPreview, loading: loadingPreview } = useRequest(
    `/email-templates/${id}/preview`,
    "GET"
  );
  const { makeRequest: approveTemplate, loading: approving } = useRequest(
    `/email-templates/${id}/approve`,
    "POST"
  );

  const saving = creating || updating;

  // Category options always include whatever the record already uses
  const categoryOptions = useMemo(() => {
    const opts = [...KNOWN_CATEGORIES] as string[];
    if (template?.category && !opts.includes(template.category))
      opts.push(template.category);
    return opts;
  }, [template]);

  const applyTemplate = (t: EmailTemplate) => {
    setTemplate(t);
    setName(t.name || "");
    setCategory(t.category || "newsletter");
    setSubject(t.subject || "");
    setBodyHtml(t.bodyHtml || "");
  };

  const loadPreview = async () => {
    if (isNew) return;
    const [res] = await getPreview();
    if (res?.data) setPreview(res.data);
  };

  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      const [res] = await getTemplate();
      if (res?.data) applyTemplate(res.data);
      setLoaded(true);
      loadPreview();
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validate = (): string | null => {
    if (!name.trim()) return "Template name is required";
    if (!category) return "Category is required";
    if (!subject.trim()) return "Subject is required";
    if (!bodyHtml.trim()) return "Email body is required";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) return showToast(err, false, { position: "top-center" });

    const payload = {
      name: name.trim(),
      category: category,
      subject: subject.trim(),
      bodyHtml,
    };

    if (isNew) {
      const [res, code] = await createTemplate(payload);
      if (code >= 200 && code < 300 && res?.data?.id) {
        showToast(toMessage(res, "Template created"), true, {
          position: "top-center",
        });
        navigate(`/email-management/templates/${res.data.id}`, {
          replace: true,
        });
      } else {
        showToast(toMessage(res, "Failed to create template"), false, {
          position: "top-center",
        });
      }
      return;
    }

    const [res, code] = await updateTemplate(payload);
    if (code >= 200 && code < 300) {
      showToast(toMessage(res, "Template saved"), true, {
        position: "top-center",
      });
      // The server sanitises HTML on save — re-read so the editor shows what
      // was actually stored, then refresh the preview.
      const [fresh] = await getTemplate();
      if (fresh?.data) applyTemplate(fresh.data);
      loadPreview();
    } else {
      showToast(toMessage(res, "Failed to save template"), false, {
        position: "top-center",
      });
    }
  };

  const handleApprove = async () => {
    const [res, code] = await approveTemplate();
    if (code >= 200 && code < 300) {
      showToast(toMessage(res, "Template approved"), true, {
        position: "top-center",
      });
      setApproveOpen(false);
      const [fresh] = await getTemplate();
      if (fresh?.data) applyTemplate(fresh.data);
    } else {
      showToast(toMessage(res, "Failed to approve template"), false, {
        position: "top-center",
      });
    }
  };

  const isApproved = template?.status === "approved";
  // The API returns 403 when the approver is the template's last editor
  const isOwnEdit =
    !!template?.lastEditedBy && template.lastEditedBy === currentAdminId();
  const canApprove = !isNew && !!template && !isApproved;
  const isOnboarding = (template?.category || "").toLowerCase() === "onboarding";

  if (!isNew && !loaded && loadingTemplate) {
    return (
      <div className="flex justify-center items-center h-96">
        <TailSpin color="skyblue" />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 mb-6 w-[95%]">
        <button onClick={() => navigate(-1)} className="cursor-pointer">
          <Icon name="arrowLeft" />
        </button>
        <h2 className="text-[22px] font-bold">
          {isNew ? "New Template" : "Edit Template"}
        </h2>
        {template?.status && <StatusPill value={template.status} />}
      </div>

      {/* Warnings that change what saving does */}
      {!isNew && isOnboarding && (
        <div className="w-[95%] mb-4 flex gap-2 items-start border border-amber-200 bg-amber-50 rounded-lg px-4 py-3">
          <LuAlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-[12px] text-amber-800">
            This is an onboarding template. Editing it pauses onboarding emails
            until it is approved again.
          </p>
        </div>
      )}
      {!isNew && isApproved && (
        <div className="w-[95%] mb-4 flex gap-2 items-start border border-amber-200 bg-amber-50 rounded-lg px-4 py-3">
          <LuAlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-[12px] text-amber-800">
            This template is approved. Saving changes resets it to{" "}
            <strong>draft</strong> and it will need approval again.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 w-[95%] items-start">
        {/* Left — editor */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelCls}>Template Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. October Newsletter"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={`${inputCls} cursor-pointer`}>
                {categoryOptions.map((c) => (
                  <option key={c} value={c} className="capitalize">
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className={labelCls}>Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Hi {{first_name}}, what's new this month"
              className={inputCls}
            />
          </div>

          <label className={labelCls}>Email Body</label>
          <RichTextEditor value={bodyHtml} onChange={setBodyHtml} />

          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0979A1] text-white rounded-lg px-5 py-2.5 text-[14px] font-semibold hover:bg-[#076a8a] disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2">
              {saving ? (
                <CircleLoader color="#ffffff" loading size={18} />
              ) : isNew ? (
                "Create Template"
              ) : (
                "Save Changes"
              )}
            </button>
            {canApprove && (
              <button
                onClick={() => setApproveOpen(true)}
                disabled={isOwnEdit || saving}
                title={
                  isOwnEdit
                    ? "Requires a different admin — you made the last edit"
                    : "Approve this template"
                }
                className="px-5 py-2.5 border border-[#0979A1] text-[#0979A1] rounded-lg text-[14px] font-semibold hover:bg-[#E8F7FA] disabled:opacity-50 disabled:cursor-not-allowed">
                Approve
              </button>
            )}
            <button
              onClick={() => navigate("/email-management", { state: { tab: "Templates" } })}
              className="px-5 py-2.5 border border-gray-200 rounded-lg text-[14px] hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>

        {/* Right — live preview */}
        <EmailPreviewPane
          preview={preview}
          loading={loadingPreview}
          onRefresh={isNew ? undefined : loadPreview}
          emptyLabel={
            isNew
              ? "Create the template to generate a preview."
              : "No preview available yet."
          }
        />
      </div>

      <ConfirmModal
        visible={approveOpen}
        title="Approve template"
        message={
          <>
            Approve <strong>{name || "this template"}</strong>? Once approved it
            can be used to send campaigns, and onboarding emails resume if this
            is an onboarding template.
          </>
        }
        confirmLabel="Approve"
        loading={approving}
        onConfirm={handleApprove}
        onClose={() => setApproveOpen(false)}
      />
    </>
  );
};

export default TemplateEditor;
